/**
 * Klien **`device-agent`** — satu-satunya jalan dari layar ke kabel printer.
 *
 * ### ⚠️ Kenapa `http://127.0.0.1` boleh dipanggil dari halaman HTTPS
 * Peramban memperlakukan `127.0.0.1`/`localhost` sebagai **potentially trustworthy origin**, jadi
 * ia tak dihitung sebagai mixed content. Itu bukan celah yang kita manfaatkan diam-diam — itu
 * memang jalur resmi untuk perangkat lokal, dan satu-satunya yang tersedia: peramban tak bisa
 * membuka soket TCP ke printer sendiri.
 *
 * ### ⛔⛔ Layar TIDAK menyusun byte
 * Yang dikirim ke agen adalah `bytesBase64` **apa adanya dari server**. Menyusunnya di sini berarti
 * `ESC p` (tendangan laci) jadi keputusan klien — bandingkan `drawer:open` yang menuntut PIN ulang.
 *
 * ### ⛔ Degradasi WAJIB TERLIHAT (K5)
 * Agen yang tak ada mengembalikan alasan yang bisa dibaca kasir, **bukan** `undefined` yang
 * ditelan. Terminal yang diam-diam berhenti mencetak adalah terminal yang tetap dipakai sampai ada
 * tamu yang minta struk.
 */

/** Alamat agen. ⚠️ Bisa ditimpa per-mesin — port bentrok itu hal biasa di mesin kasir. */
const AGENT_URL =
  globalThis.localStorage?.getItem('osbpos.agent') ?? 'http://127.0.0.1:9110';

export interface AgentResult {
  readonly ok: boolean;
  /** Kode galat yang bisa dicocokkan; `AGENT-000-OFFLINE` bila agennya sendiri tak terjangkau. */
  readonly code?: string;
  readonly message?: string;
  readonly bytesSent?: number;
}

/**
 * Mengirim byte ke printer lewat agen lokal.
 *
 * ⚠️ **Tak pernah melempar.** Pemanggilnya selalu berada sesudah uang diterima; pengecualian yang
 * naik dari sini akan membatalkan alur yang sudah sah.
 */
export async function printViaAgent(job: {
  bytesBase64: string;
  address: unknown;
}): Promise<AgentResult> {
  try {
    /*
     * ⛔⛔ **Batas waktu WAJIB** (2026-08-25) — alasan yang sama dengan `agentHealth`: permintaan ke
     * loopback bisa MENGGANTUNG, bukan gagal (mis. Chrome menahan preflight Private Network
     * Access). Sejak tendangan laci di-`await` sebelum bill ditutup, gantungan itu berarti
     * penutupan yang tak pernah terjadi — tamu berdiri di depan kasir yang layarnya membeku.
     * ⚠️ 5 detik, bukan 3: mengirim byte struk memang lebih lama daripada satu ping.
     */
    const r = await fetch(`${AGENT_URL}/agent/print`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(job),
      signal: AbortSignal.timeout(5000),
    });
    const d = (await r.json()) as { bytesSent?: number; error?: string; message?: string };
    if (!r.ok) {
      return { ok: false, code: d.error ?? `AGENT-${String(r.status)}`, ...(d.message === undefined ? {} : { message: d.message }) };
    }
    return { ok: true, ...(d.bytesSent === undefined ? {} : { bytesSent: d.bytesSent }) };
  } catch {
    /*
     * ⛔ `fetch` yang menolak = agen tak berjalan (atau port lain). Itu keadaan yang WAJAR di
     * pengembangan dan **tidak wajar** di terminal kasir — dan bedanya cuma bisa dinilai orang
     * yang berdiri di sana. ⇒ katakan apa adanya, jangan tebak.
     */
    return {
      ok: false,
      code: 'AGENT-000-OFFLINE',
      message: `agen cetak tak menjawab di ${AGENT_URL}`,
    };
  }
}

/**
 * Ping agen — dipakai lencana kesehatan. ⚠️ Tak pernah melempar.
 *
 * ⛔⛔ Mengembalikan TIGA keadaan, bukan boolean. Agen yang hidup **tanpa printer ter-pair** akan
 * menjawab `ok`, dan kalau itu dipetakan jadi hijau, lencananya berbohong tepat di keadaan yang
 * paling sering terjadi saat pemasangan pertama: aplikasi sudah dipasang, printernya belum.
 * Kegagalannya lalu muncul di tamu pertama yang minta struk.
 *
 * ⚠️ `ready` tetap **tidak** berarti printernya berkertas — itu hanya terbukti dari mencetak.
 */
/**
 * ⛔ `rejected` DIPISAH dari `down`, dan itu bukan kehalusan: agen yang menolak origin dan agen yang
 * tak ada terlihat sama di layar, padahal obatnya berbeda total — yang satu mengisi satu kolom,
 * yang satu memasang aplikasi.
 */
export type AgentState = 'down' | 'rejected' | 'no-printer' | 'ready';

export async function agentHealth(): Promise<AgentState> {
  try {
    /*
     * ⛔⛔ **Batas waktu WAJIB.** `fetch` tak punya timeout bawaan, dan permintaan ke loopback bisa
     * MENGGANTUNG (bukan gagal) — mis. saat Chrome menahan preflight Private Network Access.
     * Tanpa batas ini lencananya berhenti di "Memeriksa…" selamanya: keadaan yang tak mengabarkan
     * apa pun, dan tak bisa dibedakan dari agen yang mati (dilaporkan Imron 2026-08-22).
     */
    const r = await fetch(`${AGENT_URL}/agent/health`, { signal: AbortSignal.timeout(3000) });
    /* ⛔ Agen ADA, tapi origin layar ini tak terdaftar padanya. */
    if (r.status === 403) return 'rejected';
    if (!r.ok) return 'down';
    const d = (await r.json()) as { printerReady?: unknown };
    /*
     * ⚠️ `undefined` ⇒ dianggap SIAP. Agen Node (desktop) tak melaporkan bidang ini dan memang tak
     * bisa: printernya bisa Ethernet, dan "ter-pair" tak punya arti di sana. Menganggapnya belum
     * siap akan memerahkan setiap terminal desktop yang justru berfungsi.
     */
    return d.printerReady === false ? 'no-printer' : 'ready';
  } catch {
    return 'down';
  }
}

export { AGENT_URL };


/** Satu printer Bluetooth yang dilihat agen di daftar ter-pair OS. */
export interface AgentPrinter {
  readonly mac: string;
  readonly name: string;
  /** ⚠️ PETUNJUK, bukan keputusan: printer termal murah sering melaporkan diri `UNCATEGORIZED`. */
  readonly likelyPrinter: boolean;
}

/**
 * ⭐⭐⭐ **Printer yang DILIHAT agen** — supaya MAC tak perlu diketik siapa pun.
 *
 * ⛔ Agen tak menulis ke portal sendiri (ia mendengarkan loopback dan tak punya kredensial — K27).
 * Ia melapor; layar inilah yang mengadopsinya dengan token perangkat.
 *
 * ⚠️ Gagal ⇒ daftar KOSONG, bukan lemparan: agen yang belum terpasang adalah keadaan normal di
 * tablet baru, dan layar sudah punya lencana sendiri untuk mengatakannya.
 */
export async function agentPrinters(): Promise<readonly AgentPrinter[]> {
  try {
    const r = await fetch(`${AGENT_URL}/agent/printers`, { signal: AbortSignal.timeout(3000) });
    if (!r.ok) return [];
    const d = (await r.json()) as { printers?: AgentPrinter[] };
    return d.printers ?? [];
  } catch {
    return [];
  }
}
